const View = require('public_framework/view')
const Chart = require('chart.js')
const _ = require('underscore')
const $ = require('jquery')
const getConfig = require('./config/')
const getChartOptions = require('./helpers/chart_options')
const getDrawValue = require('./helpers/draw_value')
const { getSegmentOverlap } = require('./helpers/segment_tools')

const types = {
  'bar': 'horizontalBar',
  'pie': 'pie'
}

/**
 * Emitted to indicate that chart canvas sizes might have changed.
 * @type {String}
 */
const eventRendered = 'charts:rendered'

/**
 * Chart implementation based on Chart.js
 * It takes parameters from the public/shared/_chart.html.slim partial
 * invocation. Take a look at it for more details.
 */
class ChartView extends View {
  constructor (...args) {
    super(...args)

    this.options = this.$el.data()
    this.config = getConfig(this.options.config)

    const labels = _.keys(this.options.data)
    const values = _.values(this.options.data)
    const overrideColors = this.options.colors || {}
    const colors = labels.map(label => this.config[overrideColors[label]] || this.config.colorFirst)

    const canvas = this.$canvas()
    this.setCanvasSize(canvas, labels.length)

    const options = getChartOptions({
      config: this.config,
      type: types[this.options.type],
      labels,
      data: this.getDataForAnimation(values),
      colors,
      xAxisLabel: this.options.xLabel,
      isNarrow: this.getIsNarrow(),
      plugins: this.getPlugins()
    })

    this.chart = new Chart(canvas, options)

    if (this.config.animateWhenVisible) {
      this.realValues = values
      this.bindHandlers()
    }
  }

  unload () {
    $(window).off('scroll.charts', this.onScroll)
    $(window).off('resize.charts', this.onResize)
  }

  bindHandlers () {
    this.onScroll = _.throttle(this.animateIfVisible.bind(this), 100)
    this.onResize = _.throttle(this.saveDimensions.bind(this), 100)

    $(window).on('scroll.charts', this.onScroll)
    $(window).on('resize.charts', this.onResize)

    this.saveDimensions()
    this.animateIfVisible()
  }

  get els () {
    return {
      $canvas: '@canvas',
      $container: '@container'
    }
  }

  getDataForAnimation (values) {
    return this.config.animateWhenVisible
      ? values.map(() => 0)
      : values
  }

  setCanvasSize (canvas, bars) {
    canvas.attr('width', this.$container().width())
    canvas.attr('height', this.getHeight(this.options.type, bars))
  }

  getHeight (type, bars) {
    const { pieChartHeight, minBarChartHeight, barLineHeight, legendLineHeight } = this.config

    if (type === 'bar') {
      return Math.max(minBarChartHeight, barLineHeight * bars)
    } else if (type === 'pie') {
      return this.getIsNarrow()
        ? pieChartHeight + legendLineHeight * bars
        : pieChartHeight
    }
  }

  getIsNarrow () {
    return this.$container().width() < this.config.narrowThreshold
  }

  getPlugins () {
    let plugins = [{
      afterRender: () => this.emit(eventRendered)
    }]

    if (this.options.type === 'bar') {
      plugins.push({
        afterDatasetsDraw: getDrawValue({
          fontSize: this.config.fontSizeBase,
          fontWeight: this.config.fontWeight,
          fontFamily: this.config.fontFamily,
          color: this.config.colorCategory,
          isNarrow: this.getIsNarrow(),
          unitLabel: this.config.unitLabel
        })
      })
    }

    return plugins
  }

  saveDimensions () {
    this.offset = this.$container().offset().top
  }

  animateIfVisible () {
    const viewportTop = $(window).scrollTop()
    const viewportBottom = viewportTop + window.innerHeight
    const chartHeight = this.$canvas().height()
    const chartTop = this.offset
    const chartBottom = chartTop + chartHeight

    const chart = { from: chartTop, to: chartBottom }
    const viewport = { from: viewportTop, to: viewportBottom }
    const overlap = getSegmentOverlap(chart, viewport)

    const isOverlapped = overlap >= this.config.visibilityThreshold
    const isVisibleEnough = overlap * chartHeight >= window.innerHeight * this.config.visibilityThreshold

    if (isOverlapped || isVisibleEnough) {
      this.animate()
      this.unload()
    }
  }

  animate () {
    this.chart.data.datasets[0].data = this.realValues
    this.chart.update()
  }
}

module.exports = ChartView



// WEBPACK FOOTER //
// ./front/apps/public/shared/components/chart/index.js